import api from 'lib/trpcClient';
import { useDisclosure } from 'further-ui/hooks';
import { Typography } from '@mui/material';
import { Delete } from '@mui/icons-material';
import Table, { type Column } from 'further-ui/components/Table';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { Column as LayoutColumn } from 'further-ui/components/Layout';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

type Props = {
  vehicleId: string;
  companyId: string;
};

const RevaluationHistory: React.FC<Props> = ({ vehicleId, companyId }) => {
  const deleteDisclosure = useDisclosure<{ revaluationGroupId: string }>();

  const [revaluationHistory, query] =
    api.lpRevaluation.getRevaluationHistory.useSuspenseQuery({
      vehicleId,
      assetId: companyId,
    });

  const utils = api.useUtils();
  const mutationOpts = useHandleTrpcMutation();
  const { mutate: deleteGroup, isPending: isDeleting } =
    api.lpRevaluation.deleteGroup.useMutation(
      mutationOpts({
        successMessage: 'Revaluation deleted successfully.',
        onSuccess: () => {
          utils.lpRevaluation.getRevaluationHistory.invalidate();
          deleteDisclosure.onClose();
        },
      }),
    );

  const handleDelete = () => {
    if (deleteDisclosure.actionProps.revaluationGroupId) {
      deleteGroup({
        vehicleId,
        revaluationGroupId: deleteDisclosure.actionProps.revaluationGroupId,
      });
    }
  };

  type Row = (typeof revaluationHistory)[number] & { id: string };

  const rows: Array<Row> = revaluationHistory.map((row) => ({
    ...row,
    id: row.revaluationGroupId,
  }));

  const columns: Array<Column<Row>> = [
    {
      label: 'Revaluation date',
      key: 'ledgerDate',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      label: 'Holding classes',
      key: 'holdingClasses',
      disableSorting: true,
      render: (row) =>
        row.holdingClasses.map((hc) => hc.holdingClassName).join(', '),
    },
    {
      label: 'Total units',
      key: 'totalUnits',
      disableSorting: true,
      render: (row) => numberToDisplayString(row.totalUnits),
    },
    {
      label: 'Total hold value (LCY)',
      key: 'totalHoldValue',
      disableSorting: true,
      render: (row) =>
        numberToCurrencyString(row.totalHoldValue, { currency: row.currency }),
    },
  ];

  return (
    <LayoutColumn spacing="sm">
      <Typography variant="h5">Revaluations history</Typography>
      <Table
        scrollable
        emptyMessage="No revaluations history"
        columns={columns}
        isLoading={query.isFetching}
        rows={rows}
        rowsTotalCount={rows.length}
        actions={[
          {
            icon: Delete,
            label: 'Delete revaluation',
            disabled: (row) => !row.isDeletable,
            tooltip: (row) => row.notDeletableReason,
            onClick: (row) =>
              deleteDisclosure.stageAction({
                revaluationGroupId: row.revaluationGroupId,
              }),
          },
        ]}
      />
      <ConfirmDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        loading={isDeleting}
        title="Delete revaluation?"
        content="Are you sure you want to delete this revaluation? This will also delete any associated unrealised gain/loss actions and any draft equalisations, and cannot be undone."
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
          },
        }}
      />
    </LayoutColumn>
  );
};

export default RevaluationHistory;
